
/*
 * ----------------------------------------------------------------------------------
 * Imports - External
 * ----------------------------------------------------------------------------------
 */

import { Box, Dialog, DialogContent, DialogTitle, styled, Typography } from '@mui/material';
import {
    Checkbox,
    CollaboratingGroupContext,
    CrfCondition,
    CrfForm,
    DatePicker,
    Field,
    FormBreadcrumbs,
    FormOptionsContext,
    FormSubmitButton,
    GetFieldLookupItem,
    GroupedField,
    GroupedFields,
    GroupedTableArrayField,
    ICrfConditionParameters,
    IGroupedTableArrayFieldColumn,
    InputOptionMode,
    InstitutionContext,
    MasterGroupContext,
    Numeric,
    PageLayout,
    PatientContext,
    RouteLoading,
    Select,
    Text,
    ToggleButtonGroup
} from '@ngt/opms';
import { Permission, usePermissionsByIds } from '@ngt/opms-bctapi';
import { cloneDeep } from 'lodash-es';
import { createContext, FC, FunctionComponent, useCallback, useContext, useEffect, useMemo, useState } from 'react';


/*
 * ----------------------------------------------------------------------------------
 * Imports - Internal
 * ----------------------------------------------------------------------------------
 */
import { ConditionMode, useFormActions, ValidateOn } from '@ngt/forms-core';
import * as Dtos from '../../../api/dtos';
import { AppSettings } from '../../../AppSettings';
import useBreastMriLesionLookup from '../../../hooks/lookups/useBreastMriLesionLookup';
import InputDisplay from '../inputs/InputDisplay';
import SelectWithTooltip from '../inputs/SelectWithTooltip';
import FormLookupsModifier from '../utilities/FormLookupsModifierProps';


/*
 * ----------------------------------------------------------------------------------
 * Interface
 * ----------------------------------------------------------------------------------
 */

interface IContrastEnhancedMammogramFormProps { }

/*
 * ---------------------------------------------------------------------------------
 * Styles
 * ---------------------------------------------------------------------------------
 */


/*
 * ---------------------------------------------------------------------------------
 * Functions
 * ---------------------------------------------------------------------------------
 */
const cemPerformed = ({ formState }: ICrfConditionParameters<Dtos.ContrastEnhancedMammogram>) => formState?.value?.cemPerformed === Dtos.LookupYesNoType.Yes;
const mriLesionsNotFoundInCem = ({ formState }: ICrfConditionParameters<Dtos.ContrastEnhancedMammogram>) => formState?.value?.mriLesionsNotFoundInCem === Dtos.LookupYesNoType.Yes;
const mriOtherNotFoundInCem = ({ formState }: ICrfConditionParameters<Dtos.ContrastEnhancedMammogram>) => formState?.value?.mriOtherNotFoundInCem === true;
const pathologySpecify = ({ formState, parentFieldName }: ICrfConditionParameters<Dtos.ContrastEnhancedMammogram>) => { 
    const regex = /additionalLesions\[(?<Index>[0-9]*)]/;
    const res = regex.exec(parentFieldName ?? "");
    const idx = res?.groups?.['Index'];

    if (!idx) return false;

    const pathology = formState?.value?.additionalLesions?.[+idx]?.pathology;

    return pathology === Dtos.LookupPathologyType.Other;
}

/*
 * ----------------------------------------------------------------------------------
 * Components
 * ----------------------------------------------------------------------------------
 */

const StyledSubheading = styled(Typography)(({ theme }) => ({
    padding: theme.spacing(3, 2, 1, 2)
    
}))

const StyledDialogFormTitle = styled(DialogTitle)(({ theme }) => ({
    color: theme.palette.secondary.main,
    borderBottom: 'solid rgb(224, 224, 224) 1px',

    '& *': {
        fontSize: '1.5rem !important'
    }
    
}))

const StyledDialogButton = styled(Box)(({ theme }) => ({
    textAlign: 'right',
    padding: theme.spacing(2),

    '& > :first-of-type': {
        marginRight: theme.spacing(1),
    }
    
}))

const breastMriLesionsKey = "additionalLesions";

interface IAdditionalLesionContext {
    index: number | undefined;
    setIndex: (index: number | undefined) => void;
    openSampleDialog: boolean;
    setOpenSampleDialog: (open: boolean) => void;
}

const AdditionalLesionContext = createContext<IAdditionalLesionContext>({
    setIndex: () => { },
    index: undefined,
    openSampleDialog: false,
    setOpenSampleDialog: () => { }
});

const AdditionalLesionColumns: Array<IGroupedTableArrayFieldColumn<Dtos.BreastMriAdditionalLesion>> = [
    {
        propertyPath: 'lesionNumber',
        TableCellProps: {
            sx: {
                minWidth: 75,
                maxWidth: 75,
                width: 75
            }
        },
        content: (
            <Typography>
                CM<InputDisplay />
            </Typography>
        )
    },
    {
        propertyPath: 'lesionType',
        TableCellProps: {
            sx: {
                minWidth: 100,
                maxWidth: 100,
                width: 100
            }
        },
        content: (
            <InputDisplay
                convert={(fieldValue: any, lookups) => GetFieldLookupItem(lookups, `${breastMriLesionsKey}.lesionType`, fieldValue)?.value}
            />
        )
    },
    {
        propertyPath: 'lesionSide',
        TableCellProps: {
            sx: {
                minWidth: 75,
                maxWidth: 75,
                width: 75
            }
        },
        content: (
            <InputDisplay
                convert={(fieldValue: any, lookups) => GetFieldLookupItem(lookups, `${breastMriLesionsKey}.lesionSide`, fieldValue)?.value}
            />
        )
    },
    {
        propertyPath: 'lesionLocation',
        TableCellProps: {
            sx: {
                minWidth: 100,
                maxWidth: 100,
                width: 100
            }
        },
        content: (
            <InputDisplay
                convert={(fieldValue: any, lookups) => GetFieldLookupItem(lookups, `${breastMriLesionsKey}.lesionLocation`, fieldValue)?.value}
            />
        )
    },
    {
        propertyPath: 'lesionSize',
        TableCellProps: {
            sx: {
                minWidth: 100,
                maxWidth: 100,
                width: 100
            }
        },
        content: (
            <InputDisplay
                convert={(fieldValue: any) => fieldValue}
            />
        )
    },
    {
        propertyPath: 'birads',
        TableCellProps: {
            sx: {
                minWidth: 100,
                maxWidth: 100,
                width: 100
            }
        },
        content: (
            <InputDisplay
                convert={(fieldValue: any, lookups) => GetFieldLookupItem(lookups, `${breastMriLesionsKey}.birads`, fieldValue)?.value}
            />
        )
    },
    {
        propertyPath: 'biopsyMethod',
        TableCellProps: {
            sx: {
                minWidth: 160,
                maxWidth: 160,
                width: 160
            }
        },
        content: (
            <InputDisplay
                convert={(fieldValue: any, lookups) => GetFieldLookupItem(lookups, `${breastMriLesionsKey}.biopsyMethod`, fieldValue)?.value}
            />
        )
    },
    {
        propertyPath: 'pathology',
        TableCellProps: {
            sx: {
                minWidth: 160,
                maxWidth: 160,
                width: 160
            }
        },
        content: (
            <InputDisplay
                convert={(fieldValue: any, lookups) => GetFieldLookupItem(lookups, `${breastMriLesionsKey}.pathology`, fieldValue)?.value}
            />
        )
    },
    {
        propertyPath: 'grade',
        TableCellProps: {
            sx: {
                minWidth: 100,
                maxWidth: 100,
                width: 100
            }
        },
        content: (
            <InputDisplay
                convert={(fieldValue: any, lookups) => GetFieldLookupItem(lookups, `${breastMriLesionsKey}.grade`, fieldValue)?.value}
            />
        )
    },
    {
        propertyPath: 'estrogenReceptor',
        TableCellProps: {
            sx: {
                minWidth: 100,
                maxWidth: 100,
                width: 100
            }
        },
        content: (
            <InputDisplay
                convert={(fieldValue: any, lookups) => GetFieldLookupItem(lookups, `${breastMriLesionsKey}.estrogenReceptor`, fieldValue)?.value}
            />
        )
    },
    {
        propertyPath: 'progesteroneReceptor',
        TableCellProps: {
            sx: {
                minWidth: 100,
                maxWidth: 100,
                width: 100
            }
        },
        content: (
            <InputDisplay
                convert={(fieldValue: any, lookups) => GetFieldLookupItem(lookups, `${breastMriLesionsKey}.progesteroneReceptor`, fieldValue)?.value}
            />
        )
    },
    {
        propertyPath: 'her2Ihc',
        TableCellProps: {
            sx: {
                minWidth: 100,
                maxWidth: 100,
                width: 100
            }
        },
        content: (
            <InputDisplay
                convert={(fieldValue: any, lookups) => GetFieldLookupItem(lookups, `${breastMriLesionsKey}.her2Ihc`, fieldValue)?.value}
            />
        )
    },
    {
        propertyPath: 'her2Ish',
        TableCellProps: {
            sx: {
                minWidth: 100,
                maxWidth: 100,
                width: 100
            }
        },
        content: (
            <InputDisplay
                convert={(fieldValue: any, lookups) => GetFieldLookupItem(lookups, `${breastMriLesionsKey}.her2Ish`, fieldValue)?.value}
            />
        )
    }
];

const AdditionalLesionGrid: FC = () => {
    const { getFieldValue, setFieldValue, submit, getErrors } = useFormActions<Dtos.ContrastEnhancedMammogram, Dtos.IFormValidationError>();

    const { readOnly } = useContext(FormOptionsContext);
    const { index, setIndex, openSampleDialog, setOpenSampleDialog } = useContext(AdditionalLesionContext);

    const [previousValue, setPreviousValue] = useState<Dtos.BreastMriAdditionalLesion | undefined>(undefined);

    useEffect(() => {
        const v = getFieldValue<Dtos.BreastMriAdditionalLesion>(`${breastMriLesionsKey}[${index}]`);

        setPreviousValue(!v ? undefined : cloneDeep(v));
    }, [index, setPreviousValue, getFieldValue]);

    const onRowClick = useCallback((_value: Dtos.BreastMriAdditionalLesion | undefined | null, index: number, _values: Dtos.BreastMriAdditionalLesion[] | undefined | null) => {
        setPreviousValue(undefined);
        setIndex(index);
        setOpenSampleDialog(true);
    }, [setIndex, setPreviousValue, setOpenSampleDialog]);

    const onOk = useCallback(async () => {
        setOpenSampleDialog(false);
        setPreviousValue(undefined);
        setIndex(undefined);
    }, [setIndex, setPreviousValue, setFieldValue, setOpenSampleDialog, getErrors, submit, index]);

    const onCancel = useCallback(() => {
        setFieldValue(`${breastMriLesionsKey}[${index}]`, previousValue, true, false, false, false);
        
        setPreviousValue(undefined);
        setIndex(undefined);
        setOpenSampleDialog(false);
    }, [previousValue, breastMriLesionsKey, setFieldValue, index, setIndex, setPreviousValue, setOpenSampleDialog]);


    return (
        <>
            <StyledSubheading
                variant="h2"
            >
                Additional Lesion/s on CEM but not on MRI – Biopsy and Pathology details 
            </StyledSubheading>
            <GroupedTableArrayField
                propertyPath={breastMriLesionsKey}
                columns={AdditionalLesionColumns}
                entityName="Lesion"
                onRowClick={onRowClick}
                hideAddButton
                hideDeletion
            />

            <Dialog
                open={openSampleDialog && index != null}
                onClose={(_event, reason) => {
                    if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
                        return;
                    }
                    
                    setOpenSampleDialog(false)
                }}
                aria-labelledby="form-dialog"
                maxWidth="md"
                scroll="body"
                fullWidth
                disableEscapeKeyDown
            >
                <StyledDialogFormTitle>Additional Lesion</StyledDialogFormTitle>
                {
                    index !== null && index !== undefined && (
                        <DialogContent style={{ padding: '0' }}>
                            <Field propertyPath={`${breastMriLesionsKey}[${index}]`}>
                                <GroupedField
                                    propertyPath="lesionNumber"
                                    component={Numeric}
                                    disabled
                                />
                                <GroupedField
                                    propertyPath="lesionType"
                                    component={Select}
                                />
                                <GroupedField
                                    propertyPath="lesionSide"
                                    component={Select}
                                />
                                <GroupedField
                                    propertyPath="lesionLocation"
                                    component={Select}
                                />
                                <GroupedField
                                    propertyPath="lesionSize"
                                    component={Numeric}
                                />
                                <GroupedField
                                    propertyPath="birads"
                                    component={Select}
                                />
                                <GroupedField
                                    propertyPath="biopsyMethod"
                                    component={SelectWithTooltip}
                                    tooltipProps={{
                                        arrow: true,
                                        placement: "left",
                                        title: "If no lesion is evident at time of attempted biopsy, the lesion should be reclassified as BIRADS 2 and participant remains potentially eligible"
                                    }}
                                />
                                <GroupedFields>
                                    <GroupedField
                                        propertyPath="pathology"
                                        component={Select}
                                    />
                                    <CrfCondition
                                        mode={ConditionMode.Show}
                                        condition={pathologySpecify}
                                        subscription={{ value: true }}
                                    >
                                        <GroupedField
                                            propertyPath="pathologySpecify"
                                            component={Text}
                                        />
                                    </CrfCondition>
                                </GroupedFields>
                                <GroupedField
                                    propertyPath="grade"
                                    component={Select}
                                />
                                <GroupedField
                                    propertyPath="estrogenReceptor"
                                    component={ToggleButtonGroup}
                                />
                                <GroupedField
                                    propertyPath="progesteroneReceptor"
                                    component={ToggleButtonGroup}
                                />

                                <GroupedField
                                    propertyPath="her2Ihc"
                                    component={Select}
                                />
                                <GroupedField
                                    propertyPath="her2Ish"
                                    component={Select}
                                />
                            
                                <StyledDialogButton>
                                    <FormSubmitButton
                                        variant="text"
                                        color="secondary"
                                        onClick={onCancel}
                                    >
                                        {!readOnly ? 'Cancel' : 'Back'}
                                    </FormSubmitButton>
                                    {
                                        !readOnly && (
                                            <FormSubmitButton
                                                variant="text"
                                                color="primary"
                                                onClick={onOk}
                                            >
                                                Ok
                                            </FormSubmitButton>
                                        )
                                    }
                                </StyledDialogButton>
                            </Field>
                        </DialogContent>
                    )
                }
            </Dialog>
        </>
    );
}

const ContrastEnhancedMammogramForm: FunctionComponent<IContrastEnhancedMammogramFormProps> = () => {

    const { data: masterGroup } = useContext(MasterGroupContext);
    const { data: collaboratingGroup } = useContext(CollaboratingGroupContext);
    const { data: institution } = useContext(InstitutionContext);
    const { data: patient } = useContext(PatientContext);

    const {
        permissions: [
            canUpdateCrf
        ],
        loading: permissionsLoading
    } = usePermissionsByIds(
        [
            Permission.OpmsCrfUpdate
        ], 
        masterGroup?.id, 
        collaboratingGroup?.id, 
        institution?.id, 
        patient?.id
    );

    const [index, setIndex] = useState<number | undefined>(undefined);
    const [openSampleDialog, setOpenSampleDialog] = useState<boolean>(false);

    const lesionContext: IAdditionalLesionContext = useMemo(() => {
        return {
            index,
            setIndex: (i) => setIndex(i),
            openSampleDialog: openSampleDialog,
            setOpenSampleDialog: (open) => setOpenSampleDialog(open),
        }
    }, [index, setIndex, openSampleDialog, setOpenSampleDialog]);

    const { data: lesionLookup } = useBreastMriLesionLookup(`${breastMriLesionsKey}.breastLesionId`, patient?.id);

    const modifyLookups = useCallback((lookups?: Dtos.ILookup[]) => {
        if (!lesionLookup) {
            return lookups;
        }

        const output: Dtos.ILookup[] = [
            ...(lookups ?? []), 
            lesionLookup
        ];

        return output;
    }, [lesionLookup]);

    if (permissionsLoading) {
        return (
            <RouteLoading />
        );
    }

    return (
        <PageLayout
            breadcrumbs={<FormBreadcrumbs />}
        >
            <CrfForm
                validateOn={ValidateOn.onChange}
                readOnly={!canUpdateCrf}
                debug={AppSettings.debug}
            >
                <GroupedFields>
                    <GroupedField 
                        propertyPath="cemPerformed"
                        component={ToggleButtonGroup}
                    />
                    <CrfCondition
                        mode={ConditionMode.Enable}
                        condition={cemPerformed}
                        subscription={{ value: true }}
                    >
                        <GroupedField 
                            disableFuture
                            propertyPath="cemPerformedDate"
                            component={DatePicker}
                        />
                    </CrfCondition>
                </GroupedFields>

                <CrfCondition
                    mode={ConditionMode.Show}
                    condition={cemPerformed}
                    subscription={{ value: true }}
                >
                    <GroupedField 
                        propertyPath="cemLesionsNotFoundInMri"
                        component={ToggleButtonGroup}
                    />
                </CrfCondition>

                <CrfCondition
                    mode={ConditionMode.Show}
                    condition={cemPerformed}
                    subscription={{ value: true }}
                >
                    <GroupedField 
                        propertyPath="mriLesionsNotFoundInCem"
                        component={ToggleButtonGroup}
                    />
                    <CrfCondition
                        mode={ConditionMode.Show}
                        condition={mriLesionsNotFoundInCem}
                        subscription={{ value: true }}
                    >
                        <GroupedFields>
                            <GroupedField 
                                propertyPath="mriR2NotFoundInCem"
                                component={Checkbox}
                                LabelProps={{
                                    label: (
                                        <Typography>
                                            Which lesions were identified on MRI but not on CEM?
                                        </Typography>
                                    )
                                }}
                                formInputOptions={{
                                    labelMode: InputOptionMode.UseCustom
                                }}
                                label='R2'
                                ContainerGridProps={{
                                    sx: {
                                        paddingBottom: `0 !important`
                                    }
                                }}
                            />
                            <GroupedField 
                                propertyPath="mriR3NotFoundInCem"
                                component={Checkbox}
                                LabelProps={{
                                    label: '' as any
                                }}
                                formInputOptions={{
                                    labelMode: InputOptionMode.UseCustom
                                }}
                                label='R3'
                                ContainerGridProps={{
                                    sx: {
                                        paddingTop: `0 !important`,
                                        paddingBottom: `0 !important`
                                    }
                                }}
                            />
                            <GroupedField 
                                propertyPath="mriR4NotFoundInCem"
                                component={Checkbox}
                                LabelProps={{
                                    label: '' as any
                                }}
                                formInputOptions={{
                                    labelMode: InputOptionMode.UseCustom
                                }}
                                label='R4'
                                ContainerGridProps={{
                                    sx: {
                                        paddingTop: `0 !important`,
                                        paddingBottom: `0 !important`
                                    }
                                }}
                            />
                            <GroupedField 
                                propertyPath="mriR5NotFoundInCem"
                                component={Checkbox}
                                LabelProps={{
                                    label: '' as any
                                }}
                                formInputOptions={{
                                    labelMode: InputOptionMode.UseCustom
                                }}
                                label='R5'
                                ContainerGridProps={{
                                    sx: {
                                        paddingTop: `0 !important`,
                                        paddingBottom: `0 !important`
                                    }
                                }}
                            />
                            <GroupedField 
                                propertyPath="mriR6NotFoundInCem"
                                component={Checkbox}
                                LabelProps={{
                                    label: '' as any
                                }}
                                formInputOptions={{
                                    labelMode: InputOptionMode.UseCustom
                                }}
                                label='R6'
                                ContainerGridProps={{
                                    sx: {
                                        paddingTop: `0 !important`,
                                        paddingBottom: `0 !important`
                                    }
                                }}
                            />
                            <GroupedField 
                                propertyPath="mriOtherNotFoundInCem"
                                component={Checkbox}
                                LabelProps={{
                                    label: '' as any
                                }}
                                formInputOptions={{
                                    labelMode: InputOptionMode.UseCustom
                                }}
                                label='Other, specify'
                                ContainerGridProps={{
                                    sx: {
                                        paddingTop: `0 !important`
                                    }
                                }}
                            />
                            <CrfCondition
                                mode={ConditionMode.Show}
                                condition={mriOtherNotFoundInCem}
                                subscription={{ value: true }}
                            >
                                <GroupedField 
                                    propertyPath="mriLesionsNotFoundInCemIdentifiedSpecify"
                                    component={Text}
                                    LabelProps={{
                                        label: '' as any
                                    }}
                                    ContainerGridProps={{
                                        sx: {
                                            paddingTop: `0 !important`
                                        }
                                    }}
                                />
                            </CrfCondition>
                        </GroupedFields>
                    </CrfCondition>
                </CrfCondition>

                <FormLookupsModifier modifyFn={modifyLookups}>
                    <AdditionalLesionContext.Provider value={lesionContext}>
                        <AdditionalLesionGrid />
                    </AdditionalLesionContext.Provider>
                </FormLookupsModifier>

            </CrfForm>
        </PageLayout>
    );
}


/*
 * ----------------------------------------------------------------------------------
 * Default Export
 * ----------------------------------------------------------------------------------
 */

export default ContrastEnhancedMammogramForm;
